/**
 * Activity dot — subtle indicator on panel headers when new data arrives.
 *
 * Monitors event:batch and analysis events, then places a small colored dot
 * on the panel header of relevant panels. Dot auto-fades after 30s or when
 * the panel is opened (whichever comes first).
 *
 * Uses CSS class .activity-dot injected via theme.ts.
 */

import { eventBus } from '@/core/events';

const FADE_AFTER_MS = 30_000;

/** Map from event type → panel IDs that should light up. */
const TYPE_TO_PANELS: Record<string, string[]> = {
  conflict: ['news-feed', 'insights', 'cii-dashboard'],
  disaster: ['news-feed', 'insights'],
  fire: ['news-feed'],
  earthquake: ['news-feed'],
  weather: ['news-feed'],
  cyber_incident: ['cyber'],
  vessel_position: ['news-feed'],
  flight_position: ['news-feed'],
  news: ['news-feed'],
};

/** Track active dots so we can remove them. */
const activeDots = new Map<string, { dot: HTMLElement; timer: ReturnType<typeof setTimeout> }>();

const cleanupFns: (() => void)[] = [];

function placeDot(panelId: string): void {
  // Don't double-place
  if (activeDots.has(panelId)) return;

  const wrapper = document.querySelector(`[data-panel-id="${panelId}"]`);
  if (!wrapper) return;

  const header = wrapper.querySelector('.panel-header');
  if (!header) return;

  // Check if panel is currently visible (don't dot visible panels)
  const el = wrapper as HTMLElement;
  if (el.style.display !== 'none') return;

  const dot = document.createElement('span');
  dot.className = 'activity-dot';
  dot.setAttribute('aria-hidden', 'true');
  header.appendChild(dot);

  const timer = setTimeout(() => {
    removeDot(panelId);
  }, FADE_AFTER_MS);

  activeDots.set(panelId, { dot, timer });
}

function removeDot(panelId: string): void {
  const entry = activeDots.get(panelId);
  if (!entry) return;

  clearTimeout(entry.timer);
  entry.dot.remove();
  activeDots.delete(panelId);
}

/** Initialize activity dot system. Returns cleanup function. */
export function initActivityDots(): () => void {
  // Clear previous (HMR safety)
  destroyActivityDots();

  // Listen for new event batches
  const unsubBatch = eventBus.on('event:batch', (events) => {
    const panelSet = new Set<string>();

    for (const event of events) {
      const panels = TYPE_TO_PANELS[event.type];
      if (panels) {
        for (const p of panels) panelSet.add(p);
      }
    }

    for (const panelId of panelSet) {
      placeDot(panelId);
    }
  });
  cleanupFns.push(unsubBatch);

  // Listen for analysis results — light up insights panel
  const unsubAnalysis = eventBus.on('analysis:complete', () => {
    placeDot('insights');
  });
  cleanupFns.push(unsubAnalysis);

  // Listen for breaking news — light up news feed
  const unsubBreaking = eventBus.on('analysis:breaking', () => {
    placeDot('news-feed');
  });
  cleanupFns.push(unsubBreaking);

  // Clear dots when panels are shown
  const unsubPanel = eventBus.on('panel:toggled', ({ panelId, visible }) => {
    if (visible) {
      removeDot(panelId);
    }
  });
  cleanupFns.push(unsubPanel);

  return destroyActivityDots;
}

/** Clean up all active dots and listeners. */
export function destroyActivityDots(): void {
  for (const [panelId] of activeDots) {
    removeDot(panelId);
  }
  activeDots.clear();

  for (const fn of cleanupFns) fn();
  cleanupFns.length = 0;
}
